/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import "./styles.css";
import FormCard from "../../../reusables/cards/FormCard";
import InputSelect from "../../../reusables/Inputs/InputSelect";
import MonacoEditor from "../../../reusables/MonacoEditor";
import { useDispatch } from "react-redux";
import fetchTokenBalancesFromCovalent, {
  fetchTokenBalancesWithFallback,
  getManualTokenEntry,
  checkDuplicatesAndValidity,
  formatToFourDecimalPlaces,
  isValidAddress,
  detectAddressManipulation,
} from "../../../../methods/helper";
import ErrorMessage from "../../../reusables/MessagePopups/ErrorMessage";
import { saveTransactionDetails } from "../../../../redux/actions/multisendAction";
import ToggleButton from "../../../reusables/Toggle";
import { useTheme } from "styled-components";
import { defineCustomTheme } from "../../../reusables/MonacoEditor/customTheme";
import { toast } from 'react-toastify';

const ProvideForm = ({
  handleNextStep,
  walletBalance,
  walletAddress,
  chainId,
  setSendNativeToken,
  sendNativeToken,
  nativeTokenSymbol,
  currentTheme,
}) => {
  // const [searchInput, setSearchInput] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [token, setToken] = useState("");
  const [listOfAddresses, setListOfAddresses] = useState("");
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const [tokenList, setTokenList] = useState([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  // const [suggestions, setSuggestions] = useState([]);
  const [errors, setErrors] = useState();
  const theme = useTheme();

  const handleContinue = async () => {
    setErrors([]);
    
    // Debug logging
    console.log("Continue button clicked - Debug info:", {
      sendNativeToken,
      token,
      listOfAddresses,
      listOfAddressesLength: listOfAddresses?.length,
      walletBalance,
      walletAddress
    });
    
    // Enhanced validation before proceeding
    try {
      if (listOfAddresses?.length <= 1) {
        setErrors(["You must enter more than one address"]);
        return;
      }

      // Create a validation promise for each address
      const validationPromises = listOfAddresses.map(async (item, index) => {
        const address = item.address;
        
        // Store original address in DOM data attribute for later comparison
        const addressElement = document.querySelector(`[data-address-index="${index}"]`);
        if (addressElement) {
          addressElement.dataset.originalAddress = address;
        }

        // Basic validation with debug info
        console.log(`Validating address at line ${index + 1}:`, {
          address,
          addressType: typeof address,
          addressLength: address?.length,
          startsWithOx: address?.startsWith?.('0x'),
          isValidResult: isValidAddress(address)
        });
        
        if (!isValidAddress(address)) {
          return `Invalid address format at line ${index + 1}: ${address}`;
        }

        // Check for manipulation
        if (addressElement && addressElement.dataset.originalAddress) {
          const originalAddress = addressElement.dataset.originalAddress;
          if (detectAddressManipulation(originalAddress, address)) {
            return `Address manipulation detected at line ${index + 1}. Transaction blocked for security.`;
          }
        }

        return null; // No error
      });

      // Wait for all validations to complete
      const validationErrors = (await Promise.all(validationPromises)).filter(error => error !== null);

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      // Check for duplicates and perform final validation
      const errors = await checkDuplicatesAndValidity(listOfAddresses);
      if (errors?.length > 0) {
        setErrors(errors);
        return;
      }

      // If all validations pass, proceed with the transaction
      const multiSendDetails = {
        sender: walletAddress,
        token: token,
        receiver: listOfAddresses,
        walletBalance: {
          nativeTOkenName: nativeTokenSymbol,
          nativeTokenBalance: walletBalance,
        },
      };

      dispatch(saveTransactionDetails(multiSendDetails));
      handleNextStep();
    } catch (error) {
      console.error("Validation error:", error);
      setErrors(["An error occurred during validation. Please try again."]);
    }
  };

  // Add real-time address validation on paste
  const handleAddressPaste = (event) => {
    try {
      const pastedText = event.clipboardData.getData('text');
      const originalAddress = pastedText.trim();
      
      // Validate the pasted address immediately
      if (!isValidAddress(originalAddress)) {
        event.preventDefault();
        toast.error("Invalid address format detected!");
        return;
      }
      
      // Store the original address for later comparison
      event.target.dataset.originalAddress = originalAddress;
      
      // Add a small delay to check for manipulation after paste
      setTimeout(() => {
        const currentAddress = event.target.value;
        if (detectAddressManipulation(originalAddress, currentAddress)) {
          toast.error("WARNING: Address manipulation detected! Transaction will be blocked.");
          event.target.classList.add('address-manipulation-detected');
        }
      }, 100);
    } catch (error) {
      console.error("Paste handling error:", error);
      toast.error("Error processing pasted address");
    }
  };

  useEffect(() => {
    async function fetchTokenBalances() {
      if (walletAddress && chainId) {
        console.log("🔄 Starting token fetch for:", walletAddress, "on chain:", chainId);
        console.log("🌐 Current network chainId:", chainId);
        
        setIsLoadingTokens(true);
        setTokenList([]); // Clear existing tokens while loading
        setToken(""); // Clear selected token when network changes
        
        try {
          const tokenListFrom = await fetchTokenBalancesWithFallback(
            walletAddress,
            chainId
          );
          
          console.log("📊 Fetched tokens for network:", chainId, tokenListFrom);
          console.log("📊 Token list length:", tokenListFrom?.length);
          
          // Validate that all tokens belong to the current network
          const networkFilteredTokens = (tokenListFrom || []).filter(token => {
            const isValidForNetwork = !token.chainId || token.chainId === chainId;
            if (!isValidForNetwork) {
              console.warn(`🚫 Filtering out token ${token.symbol} (${token.tokenAddress}) - wrong network (${token.chainId} !== ${chainId})`);
            }
            return isValidForNetwork;
          });
          
          console.log(`✅ Network-filtered tokens (${networkFilteredTokens.length}/${(tokenListFrom || []).length}):`, networkFilteredTokens);
          
          // Set the token list from API response (empty if no tokens found)
          setTokenList(networkFilteredTokens);
        } catch (error) {
          console.error("❌ Error fetching tokens:", error);
          setTokenList([]);
        } finally {
          setIsLoadingTokens(false);
        }
      } else {
        // Clear tokens if no wallet or chainId
        setTokenList([]);
        setToken("");
      }
    }
    // Call the async function
    fetchTokenBalances();
  }, [walletAddress, chainId]);

  useEffect(() => {
    defineCustomTheme(theme.body);
  }, [theme, currentTheme]);


  return (
    <FormCard>
      <div className="token_balance_container">
        <span>Token Address</span>
        <span>
          {formatToFourDecimalPlaces(walletBalance)}{" "}
          <span style={{ textTransform: "uppercase" }}>
            {nativeTokenSymbol}
          </span>
        </span>
      </div>
      <div className="token_balance_container">
        <InputSelect
          value={token}
          onChange={setToken}
          // onInputChange={handleSearchChange}
          options={tokenList}
          walletAddress={walletAddress}
          placeholder={isLoadingTokens ? "Loading tokens..." : "Paste or Select token address"}
          disabled={sendNativeToken}
          isLoading={isLoadingTokens}
        />
        <ToggleButton
          checked={sendNativeToken}
          onChange={(e) => setSendNativeToken(e.target.checked)}
          label={`Use Native Token (${nativeTokenSymbol})`}
        />
      </div>
      
      {/* Show helpful message when no tokens are loaded */}
      { walletAddress && (
        <div style={{
          background: theme.body,
          border: `1px solid ${theme.greenLight}`,
          borderRadius: "5px",
          padding: "15px",
          margin: "10px 0",
          fontSize: "14px"
        }}>
          <div style={{ color: theme.text, marginBottom: "10px" }}>
            <strong>💡 No tokens loaded from APIs</strong>
          </div>
          <div style={{ color: theme.text, marginBottom: "8px" }}>
            You have two options:
          </div>
          <ol style={{ color: theme.text, margin: "8px 0", paddingLeft: "20px" }}>
            <li><strong>Use Native Token:</strong> Toggle "{nativeTokenSymbol}" above (recommended)</li>
            <li><strong>Manual Entry:</strong> Paste a token contract address in the field above</li>
          </ol>
          
        </div>
      )}
      <div className="token_balance_container">
        <span>List of addresses in CSV</span>
        <Button
          variant="link"
          onClick={() => setShowModal(true)}
          style={{ color: theme.black }}
        >
          Show Sample CSV
        </Button>
      </div>
      <MonacoEditor
        upload={true}
        getWantedValue={setListOfAddresses}
        currentTheme={currentTheme}
        onPaste={handleAddressPaste}
        className="address-input-editor"
      />
      {errors?.length > 0 && (
        <ErrorMessage
          message={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                color: "red",
              }}
            >
              {errors?.map((error) => (
                <span key={error}>{error}</span>
              ))}
            </div>
          }
        />
      )}
      
      {/* Debug Info - Remove in production */}
      <div style={{ 
        background: theme.body, 
        padding: "10px", 
        margin: "10px 0", 
        borderRadius: "5px",
        fontSize: "12px",
        border: `1px solid ${theme.greenLight}`
      }}>
        <div><strong>Debug Info:</strong></div>
        <div>🔘 sendNativeToken: <strong>{sendNativeToken ? 'TRUE' : 'FALSE'}</strong></div>
        <div>🪙 token: <strong>{token ? `selected (${token.symbol || 'unknown'})` : 'NOT SELECTED'}</strong></div>
        <div>📝 listOfAddresses length: <strong>{listOfAddresses?.length || 0}</strong></div>
        <div>💰 walletBalance: <strong>{walletBalance || 'NOT SET'}</strong></div>
        <div>🔗 walletAddress: <strong>{walletAddress ? 'CONNECTED' : 'NOT CONNECTED'}</strong></div>
      
        <div style={{marginTop: '10px'}}><strong>Button should be:</strong> {
          ((!sendNativeToken && !token) ||
          !(listOfAddresses?.length > 0) ||
          !walletBalance ||
          !walletAddress) ? '🔴 DISABLED' : '🟢 ENABLED'
        }</div>
      </div>
      <button
        // variant="primary"
        type="submit"
        className="mt-3 button"
        onClick={handleContinue}
        disabled={
          (!sendNativeToken && !token) ||
          !(listOfAddresses?.length > 0) ||
          !walletBalance ||
          !walletAddress
        }
        style={{ background: theme.greenLight, color: theme.black }}
      >
        Continue
      </button>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header
          closeButton
          style={{
            background: theme.body,
            border: `1px solid ${theme.greenLight}`,
          }}
        >
          <Modal.Title>Sample CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            background: theme.body,
            border: `1px solid ${theme.greenLight}`,
          }}
        >
          <MonacoEditor
            initialValue={
              "0xB401bE00C3F9ddce5e9Ec0F60f2fAECC005A9728,0.000056 0xC8c30Fa803833dD1Fd6DBCDd91Ed0b301EFf87cF,13.45 0x7D52422D3A5fE9bC92D3aE8167097eE09F1b347d,1.049 0x64c9525A3c3a65Ea88b06f184F074C2499578A7E,1"
            }
            ref={editorRef}
            download={true}
            readyOnly={true}
          />
        </Modal.Body>
        <Modal.Footer
          style={{
            background: theme.body,
            border: `1px solid ${theme.greenLight}`,
          }}
        >
          <div
            className="button_container"
            style={{
              background: theme.body,
            }}
          >
            <Button
              onClick={() => setShowModal(false)}
              style={{ background: theme.greenLight, color: theme.black }}
            >
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </FormCard>
  );
};

export default ProvideForm;
